import {
  CardAttachmentAddInputSchema,
  type CardAttachmentAddInput,
  type CardAttachmentAddSuccess,
  type CardAttachmentId,
  isCardAttachmentId,
  toResponseError,
} from '@terros/common'
import { useMutation } from '@tanstack/react-query'
import { useServerRequest } from '../api'

type UseUploadCardAttachmentResult = {
  upload: (file: File) => Promise<CardAttachmentId>
  isUploading: boolean
  error: string | null
}

export function useUploadCardAttachment(): UseUploadCardAttachmentResult {
  const requestUpload = useServerRequest<CardAttachmentAddInput, CardAttachmentAddSuccess>('/card/attachment')

  const mutation = useMutation({
    mutationFn: async (file: File): Promise<CardAttachmentId> => {
      try {
        const input = CardAttachmentAddInputSchema.parse({
          contentType: file.type,
          contentLength: file.size,
        })
        const { uploadUrl } = await requestUpload(input)

        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'If-None-Match': '*',
          },
        })
        if (!response.ok) throw new Error('Photo upload failed')

        return cardAttachmentIdFromUploadUrl(uploadUrl)
      } catch (error) {
        throw toResponseError(error)
      }
    },
  })

  return {
    upload: mutation.mutateAsync,
    isUploading: mutation.isPending,
    error: mutation.error?.message ?? null,
  }
}

export function cardAttachmentIdFromUploadUrl(uploadUrl: string): CardAttachmentId {
  const segments = new URL(uploadUrl).pathname.split('/')
  const attachmentId = decodeURIComponent(segments.at(-1) ?? '')
  if (!isCardAttachmentId(attachmentId)) throw new Error('Upload URL does not contain a card attachment ID')
  return attachmentId
}
